import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { RefreshCcw, Search } from 'lucide-react';
import {
  DeploymentCustomer,
  DeploymentServer,
  DeploymentTargetRecord,
  ServerLock,
  SetupRecord,
  SetupServerGroup,
  SetupStackSummary,
  SetupTargetSummary,
  StackCandidate,
} from '../../types/deployment';
import { ServerSetupGroup } from './ServerSetupGroup';
import { SetupRunHistoryState } from './SetupHistoryPanel';
import {
  getServerGroupMetrics,
  matchesSetupSearch,
  matchesStackSearch,
  sortByRecentActivity,
  getStackStatusFilterValue,
  getTargetActionFilterValue,
  getTargetStatusFilterValue,
  getStackActionFilterValue,
} from './setupExplorerUtils';

type SetupExplorerFilters = {
  customerId: string;
  serverId: string;
  environment: string;
  status: string;
  actionType: string;
  search: string;
};

const initialFilters: SetupExplorerFilters = {
  customerId: '',
  serverId: '',
  environment: '',
  status: '',
  actionType: '',
  search: '',
};

interface SetupExplorerProps {
  customers: DeploymentCustomer[];
  servers: DeploymentServer[];
  groups: SetupServerGroup[];
  loading: boolean;
  error: string;
  saving?: boolean;
  isAdmin: boolean;
  targetHistoryById: Record<string, SetupRunHistoryState | undefined>;
  stackHistoryByKey: Record<string, SetupRunHistoryState | undefined>;
  onRefresh: () => void;
  onLoadTargetHistory: (summary: SetupTargetSummary, append?: boolean) => void;
  onLoadStackHistory: (summary: SetupStackSummary, append?: boolean) => void;
  onOpenSetup: (target: DeploymentTargetRecord) => void;
  onReprovision: (target: DeploymentTargetRecord) => void;
  onOpenManagedArtifacts: (target: DeploymentTargetRecord) => void;
  onTlsAction: (target: DeploymentTargetRecord, action: 'check' | 'issue' | 'renew-dry-run' | 'renew') => void;
  onRefreshSetup: (setupId: string) => void;
  onOpenRunDetails: (setup: SetupRecord) => void;
  onOpenForceRelease: (lock: ServerLock) => void;
  onOpenStack: (stack: StackCandidate) => void;
  onRetryStack: (stackRunId: string) => void;
}

export const SetupExplorer: React.FC<SetupExplorerProps> = ({
  customers,
  servers,
  groups,
  loading,
  error,
  saving = false,
  isAdmin,
  targetHistoryById,
  stackHistoryByKey,
  onRefresh,
  onLoadTargetHistory,
  onLoadStackHistory,
  onOpenSetup,
  onReprovision,
  onOpenManagedArtifacts,
  onTlsAction,
  onRefreshSetup,
  onOpenRunDetails,
  onOpenForceRelease,
  onOpenStack,
  onRetryStack,
}) => {
  const [filters, setFilters] = useState<SetupExplorerFilters>(initialFilters);
  const [expandedServerIds, setExpandedServerIds] = useState<Record<string, boolean>>({});
  const [openTargetHistoryById, setOpenTargetHistoryById] = useState<Record<string, boolean>>({});
  const [openStackHistoryByKey, setOpenStackHistoryByKey] = useState<Record<string, boolean>>({});

  const environmentOptions = useMemo(() => (
    Array.from(new Set(groups.flatMap((group) => [
      ...group.targetSummaries.map((summary) => summary.target.environment || ''),
      ...group.stackSummaries.map((summary) => summary.environment || ''),
    ]).filter(Boolean))).sort()
  ), [groups]);

  const filteredGroups = useMemo(() => {
    const hasFilter = Boolean(filters.status || filters.actionType || filters.search || filters.customerId || filters.serverId || filters.environment);

    return groups
      .filter((group) => {
        if (filters.customerId) {
          const groupCustomerId = typeof group.customer === 'string' ? group.customer : group.customer?._id;
          if (groupCustomerId !== filters.customerId) {
            return false;
          }
        }

        if (filters.serverId) {
          const groupServerId = typeof group.server === 'string' ? group.server : group.server?._id;
          if (groupServerId !== filters.serverId) {
            return false;
          }
        }

        return true;
      })
      .map((group) => {
        const targetSummaries = group.targetSummaries.filter((summary) => {
          if (filters.environment && summary.target.environment !== filters.environment) {
            return false;
          }
          if (filters.status && getTargetStatusFilterValue(summary) !== filters.status) {
            return false;
          }
          if (filters.actionType && getTargetActionFilterValue(summary) !== filters.actionType) {
            return false;
          }
          return matchesSetupSearch(summary, filters.search);
        });

        const stackSummaries = group.stackSummaries.filter((summary) => {
          if (filters.environment && summary.environment !== filters.environment) {
            return false;
          }
          if (filters.status && getStackStatusFilterValue(summary) !== filters.status) {
            return false;
          }
          if (filters.actionType && getStackActionFilterValue(summary) !== filters.actionType) {
            return false;
          }
          return matchesStackSearch(summary, filters.search);
        });

        return {
          ...group,
          targetSummaries,
          stackSummaries,
        };
      })
      .filter((group) => (
        group.targetSummaries.length > 0
        || group.stackSummaries.length > 0
        || (!hasFilter && group.activeLocks.length > 0)
      ))
      .sort((left, right) => {
        const recentCompare = sortByRecentActivity(left.latestActivityAt, right.latestActivityAt);
        if (recentCompare !== 0) {
          return recentCompare;
        }
        return String((left.server as DeploymentServer | null)?.name || '').localeCompare(String((right.server as DeploymentServer | null)?.name || ''));
      });
  }, [filters.actionType, filters.customerId, filters.environment, filters.search, filters.serverId, filters.status, groups]);

  const explorerSummary = useMemo(() => {
    const metrics = filteredGroups.map((group) => getServerGroupMetrics(group));
    return {
      serverCount: filteredGroups.length,
      targetCount: metrics.reduce((total, metric) => total + metric.targetCount, 0),
      stackCount: metrics.reduce((total, metric) => total + metric.stackCount, 0),
      lockCount: metrics.reduce((total, metric) => total + metric.lockCount, 0),
      activeSetups: metrics.reduce((total, metric) => total + metric.activeSetups, 0),
      failedItems: metrics.reduce((total, metric) => total + metric.failedTargets + metric.failedStacks, 0),
    };
  }, [filteredGroups]);

  const handleFilterChange = <K extends keyof SetupExplorerFilters>(key: K, value: SetupExplorerFilters[K]) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleToggleGroup = (serverId: string) => {
    setExpandedServerIds((current) => ({
      ...current,
      [serverId]: !current[serverId],
    }));
  };

  const handleToggleTargetHistory = (summary: SetupTargetSummary) => {
    const nextOpen = !openTargetHistoryById[summary.targetId];
    setOpenTargetHistoryById((current) => ({
      ...current,
      [summary.targetId]: nextOpen,
    }));

    if (nextOpen && !targetHistoryById[summary.targetId]?.loaded) {
      onLoadTargetHistory(summary, false);
    }
  };

  const handleToggleStackHistory = (summary: SetupStackSummary) => {
    const nextOpen = !openStackHistoryByKey[summary.key];
    setOpenStackHistoryByKey((current) => ({
      ...current,
      [summary.key]: nextOpen,
    }));

    if (nextOpen && !stackHistoryByKey[summary.key]?.loaded) {
      onLoadStackHistory(summary, false);
    }
  };

  return (
    <Stack spacing={3}>
      {error ? (
        <Alert severity="error">
          {error}
        </Alert>
      ) : null}

      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', lg: 'row' }} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h5" fontWeight={700}>Server-First Setup Explorer</Typography>
              <Typography variant="body2" color="text.secondary">
                See current setup health, locks, target readiness, and stack orchestration by server before drilling into historical runs.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                <Chip size="small" variant="outlined" label={`${explorerSummary.serverCount} servers`} />
                <Chip size="small" variant="outlined" label={`${explorerSummary.targetCount} targets`} />
                <Chip size="small" variant="outlined" label={`${explorerSummary.stackCount} stack lanes`} />
                {explorerSummary.lockCount > 0 ? <Chip size="small" color="warning" label={`${explorerSummary.lockCount} locks`} /> : null}
                {explorerSummary.activeSetups > 0 ? <Chip size="small" color="info" label={`${explorerSummary.activeSetups} active`} /> : null}
                {explorerSummary.failedItems > 0 ? <Chip size="small" color="error" label={`${explorerSummary.failedItems} issues`} /> : null}
              </Stack>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                variant="outlined"
                startIcon={loading ? <CircularProgress size={16} /> : <RefreshCcw size={16} />}
                onClick={onRefresh}
                disabled={loading || saving}
              >
                Refresh
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ position: 'sticky', top: 0, zIndex: 1 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                fullWidth
                label="Search setup state"
                value={filters.search}
                onChange={(event) => handleFilterChange('search', event.target.value)}
                InputProps={{
                  startAdornment: <Search size={16} style={{ marginRight: 8 }} />,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                select
                fullWidth
                label="Customer"
                value={filters.customerId}
                onChange={(event) => handleFilterChange('customerId', event.target.value)}
              >
                <MenuItem value="">All customers</MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer._id} value={customer._id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                select
                fullWidth
                label="Server"
                value={filters.serverId}
                onChange={(event) => handleFilterChange('serverId', event.target.value)}
              >
                <MenuItem value="">All servers</MenuItem>
                {servers.map((server) => (
                  <MenuItem key={server._id} value={server._id}>
                    {server.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                select
                fullWidth
                label="Environment"
                value={filters.environment}
                onChange={(event) => handleFilterChange('environment', event.target.value)}
              >
                <MenuItem value="">All environments</MenuItem>
                {environmentOptions.map((environment) => (
                  <MenuItem key={environment} value={environment}>
                    {environment}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                select
                fullWidth
                label="Current status"
                value={filters.status}
                onChange={(event) => handleFilterChange('status', event.target.value)}
              >
                <MenuItem value="">All statuses</MenuItem>
                {['not-started', 'provisioning', 'ready', 'ready-for-update', 'failed-preflight', 'partially-ready', 'failed', 'requested', 'running', 'success', 'cancelled'].map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <TextField
                select
                fullWidth
                label="Current action"
                value={filters.actionType}
                onChange={(event) => handleFilterChange('actionType', event.target.value)}
              >
                <MenuItem value="">All actions</MenuItem>
                {[
                  'setup',
                  'check',
                  'dry-run',
                  're-provision-credentials',
                  'managed-artifact-reconcile',
                  'certificate-check',
                  'tls-issue',
                  'tls-renew-dry-run',
                  'tls-renew',
                  'failed-preflight',
                  'stack-run',
                  'stack-dry-run',
                ].map((actionType) => (
                  <MenuItem key={actionType} value={actionType}>
                    {actionType}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6} lg={3}>
              <Stack direction="row" spacing={1} sx={{ height: '100%', alignItems: 'center' }}>
                <Button variant="text" onClick={() => setFilters(initialFilters)}>
                  Reset filters
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && groups.length === 0 ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
          <CircularProgress size={30} />
        </Stack>
      ) : null}

      {!loading && filteredGroups.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="body1" fontWeight={600}>
              No setup data matches the current filters.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try clearing some filters or refresh to pick up the latest server setup state.
            </Typography>
          </CardContent>
        </Card>
      ) : null}

      <Stack spacing={2}>
        {filteredGroups.map((group) => (
          <ServerSetupGroup
            key={group.serverId}
            group={group}
            expanded={Boolean(expandedServerIds[group.serverId])}
            saving={saving}
            isAdmin={isAdmin}
            targetHistoryById={targetHistoryById}
            stackHistoryByKey={stackHistoryByKey}
            openTargetHistoryById={openTargetHistoryById}
            openStackHistoryByKey={openStackHistoryByKey}
            onToggleGroup={() => handleToggleGroup(group.serverId)}
            onToggleTargetHistory={handleToggleTargetHistory}
            onToggleStackHistory={handleToggleStackHistory}
            onLoadMoreTargetHistory={(summary) => onLoadTargetHistory(summary, true)}
            onRefreshTargetHistory={(summary) => onLoadTargetHistory(summary, false)}
            onLoadMoreStackHistory={(summary) => onLoadStackHistory(summary, true)}
            onRefreshStackHistory={(summary) => onLoadStackHistory(summary, false)}
            onOpenSetup={onOpenSetup}
            onReprovision={onReprovision}
            onOpenManagedArtifacts={onOpenManagedArtifacts}
            onTlsAction={onTlsAction}
            onRefreshSetup={onRefreshSetup}
            onOpenRunDetails={onOpenRunDetails}
            onOpenForceRelease={onOpenForceRelease}
            onOpenStack={onOpenStack}
            onRetryStack={onRetryStack}
          />
        ))}
      </Stack>
    </Stack>
  );
};
